import { api } from "api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { T_Api_Paginated, T_Api_Success_Res } from "./api.types";
import { OrganisationDto, StrategyConfig, UpdateOrganisation } from "dtos/organisation.dto";
import { T_MongoId } from "types/_utils.types";
import { Resource, Stage } from "dtos/stage.dto";

const STAGES_KEY = "stages";
const STAGE_MUTATION_KEY = ["stages", "mutate"];

const stagesQueryKey = (orgCode: string | undefined) => [STAGES_KEY, orgCode];

const sameId = (a: T_MongoId | string, b: T_MongoId | string) => String(a) === String(b);

export const organisationService = {
  useGetPaginated: (params: {
    page: number;
    limit: number;
    filter?: {
      createdAt?: {
        startDate?: string;
        endDate?: string;
      };
      updatedAt?: {
        startDate?: string;
        endDate?: string;
      };
    };
    isReady?: boolean;
    sort?: { [key in keyof Partial<OrganisationDto>]: 1 | -1 } | { _id: 1 | -1 };
    search?: string;
  }) => {
    return useQuery({
      queryKey: ["organisation-all", params],
      queryFn: async () => {
        const res = await api.get(`/organisation`, { params });
        return res.data as T_Api_Success_Res<T_Api_Paginated<OrganisationDto>>;
      },
    });
  },

  useAddOrganisation: () => {
    return useMutation({
      mutationKey: ["organisation/create"],
      mutationFn: async (body: OrganisationDto) => {
        return api.post("/organisation", body);
      },
    });
  },

  useGetBySlug: (slug: string | undefined) => {
    return useQuery({
      queryKey: ["organisation", slug],
      queryFn: async () => {
        const res = await api.get(`/organisation/${slug}`);
        return res.data?.result as OrganisationDto;
      },
      enabled: !!slug,
    });
  },

  useUpdateOrganisation: () => {
    return useMutation({
      mutationKey: ["organisation/update"],
      mutationFn: async ({ slug, body }: { slug: string | undefined; body: Partial<UpdateOrganisation> }) => {
        return api.post(`/organisation/update/${slug}`, body);
      },
    });
  },

  useUpdateOrganisationStrategyConfig: () => {
    return useMutation({
      mutationKey: ["organisation/update-strategy-config"],
      mutationFn: async ({
        slug,
        body,
        field,
      }: {
        slug: string | undefined;
        body: StrategyConfig | StrategyConfig[];
        field: "custom" | "dynamic";
      }) => {
        return api.post(`/organisation/update-strategy-config/${slug}?field=${field}`, body);
      },
    });
  },

  useToggleStatusOrganisation: () => {
    return useMutation({
      mutationKey: ["organisation/status"],
      mutationFn: async ({ slug }: { slug: string }) => {
        return api.patch(`/organisation/status/${slug}`);
      },
    });
  },

  useToggleReadyOrganisation: () => {
    return useMutation({
      mutationKey: ["organisation/ready"],
      async mutationFn({ slug, value }: { slug: string; value: boolean }) {
        return api.patch(`/organisation/ready/${slug}`, { isReady: value });
      },
    });
  },
  useGetAllOrganisations: (params: Partial<{ isReady: boolean; allowed: boolean }> = {}) => {
    return useQuery({
      queryKey: ["organisations", params],
      queryFn: async () => {
        const res = await api.get(`/organisation/all/unpaginated`, { params });
        return res.data?.result as OrganisationDto[];
      },
    });
  },
  useGetAllStages: (orgCode: string | undefined, params?: Partial<{ usable: boolean; resource: Resource }>) => {
    return useQuery({
      queryKey: [...stagesQueryKey(orgCode), params?.usable],
      enabled: Boolean(orgCode),
      queryFn: async () => {
        const { data } = await api.get<T_Api_Success_Res<Stage.Dto[]>>("/organisation/stages", {
          params: { orgCode, is_usable: params?.usable },
        });

        return params?.resource ? data.result.filter((stage) => stage.resource === params.resource) : data.result;
      },
    });
  },

  useAddStage: () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationKey: STAGE_MUTATION_KEY,
      mutationFn: async ({ orgCode, payload }: { orgCode: string; payload: Stage.Create }) => {
        const { data } = await api.post<T_Api_Success_Res<Stage.Dto>>("/organisation/stages", payload, {
          params: { orgCode },
        });
        return data;
      },
      onSuccess: ({ result: created }, { orgCode }) => {
        queryClient.setQueryData<Stage.Dto[]>(stagesQueryKey(orgCode), (current) =>
          current ? [...current, created] : [created],
        );
      },
      onSettled: (_data, _error, { orgCode }) => {
        if (queryClient.isMutating({ mutationKey: STAGE_MUTATION_KEY }) === 1) {
          void queryClient.invalidateQueries({ queryKey: stagesQueryKey(orgCode) });
        }
      },
    });
  },

  useUpdateStage: () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationKey: STAGE_MUTATION_KEY,
      mutationFn: async ({ id, orgCode, payload }: { id: T_MongoId; orgCode: string; payload: Stage.Update }) => {
        const { data } = await api.patch<T_Api_Success_Res<Stage.Dto>>(`/organisation/stages/${id}`, payload, {
          params: { orgCode },
        });
        return data;
      },
      onMutate: async ({ id, orgCode, payload }) => {
        const queryKey = stagesQueryKey(orgCode);
        await queryClient.cancelQueries({ queryKey });

        const previous = queryClient.getQueryData<Stage.Dto[]>(queryKey);

        queryClient.setQueryData<Stage.Dto[]>(queryKey, (current) =>
          current?.map((stage) => (sameId(stage._id, id) ? { ...stage, ...payload } : stage)),
        );

        return { queryKey, previous };
      },
      onError: (_error, _variables, context) => {
        if (context?.previous) {
          queryClient.setQueryData(context.queryKey, context.previous);
        }
      },
      onSuccess: ({ result: updated }, { orgCode }) => {
        queryClient.setQueryData<Stage.Dto[]>(stagesQueryKey(orgCode), (current) =>
          current?.map((stage) => (sameId(stage._id, updated._id) ? updated : stage)),
        );
      },
      onSettled: (_data, _error, { orgCode }) => {
        if (queryClient.isMutating({ mutationKey: STAGE_MUTATION_KEY }) === 1) {
          void queryClient.invalidateQueries({ queryKey: stagesQueryKey(orgCode) });
        }
      },
    });
  },
};
